import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import currencyService from '../services/currencyService';

const SimpleRenewalDialog = ({ open, onClose, esimData, onConfirmRenewal, loading, error }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  // Removed: convertedPrice, converting, pricingData, loadingPricing (not needed with hardcoded prices)




  // Client pricing tiers based on data capacity
  // Primary currency: EUR (what client sets)
  // USD: Converted in real-time via backend
  const CLIENT_PRICING_TIERS = {
    // TGT E01 Profile eSIM/SIM card pricing
    "10GB": { EUR: 17.00, USD: 18.36 },
    "20GB": { EUR: 22.00, USD: 23.76 },
    "30GB": { EUR: 27.00, USD: 29.16 },
    "50GB": { EUR: 32.00, USD: 34.56 },
    
    // TGT 60-Day Pricing
    "20GB_60D": { EUR: 27.00, USD: 29.16 },  // 20GB with 60 days validity
    "60GB_60D": { EUR: 47.00, USD: 50.76 },  // 60GB with 60 days validity
    
    // AirHub Vodafone Pricing
    "200GB": { EUR: 40.00, USD: 43.20 },  // AirHub Vodafone
    "400GB": { EUR: 50.00, USD: 54.00 },  // AirHub Vodafone
    "500GB": { EUR: 120.00, USD: 129.60 },  // AirHub Vodafone
    
    // Legacy mappings (for backward compatibility)
    "50GB_120D": { EUR: 35.00, USD: 37.80 },
    "100GB": { EUR: 40.00, USD: 43.20 },
    "100GB_120D": { EUR: 45.00, USD: 48.60 },
  };

  // Extract data capacity from plan name
  const extractDataCapacity = (planName) => {
    if (!planName) return null;

    const planLower = planName.toLowerCase();

    // Check for 60 days validity (special pricing for 20GB and 60GB)
    const has60Days = /60\s*d/i.test(planLower);
    
    // Check for 120 days validity
    const has120Days = /120\s*d/i.test(planLower);

    // Extract GB amount
    const gbMatch = planLower.match(/(\d+)\s*gb/i);
    if (!gbMatch) return null;

    const gbAmount = gbMatch[1];

    // Return with validity suffix if applicable
    if (has60Days && (gbAmount === "20" || gbAmount === "60")) {
      return `${gbAmount}GB_60D`;
    } else if (has120Days && (gbAmount === "50" || gbAmount === "100")) {
      return `${gbAmount}GB_120D`;
    }

    return `${gbAmount}GB`;
  };

  // Get client price based on plan
  const getClientPrice = (planName, currency = 'USD') => {
    const capacity = extractDataCapacity(planName);

    if (!capacity || !CLIENT_PRICING_TIERS[capacity]) {
      // Default fallback (30GB pricing)
      return currency === 'EUR' ? 27.00 : 29.16;
    }

    return CLIENT_PRICING_TIERS[capacity][currency];
  };

  // Get pricing info (client price only - provider cost hidden)
  const clientPrice = esimData?.plan_name ? getClientPrice(esimData.plan_name, selectedCurrency) : 29.16;

  useEffect(() => {
    if (open) {
      fetchCurrencies();
    }
  }, [open]);

  // Remove real-time conversion - use hardcoded prices only
  // useEffect(() => {
  //   if (selectedCurrency !== 'USD') {
  //     convertPrice();
  //   } else {
  //     setConvertedPrice(null);
  //   }
  // }, [selectedCurrency, clientPrice]);

  const fetchCurrencies = async () => {
    try {
      const currencies = await currencyService.getSupportedCurrencies();
      setAvailableCurrencies(currencies);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  // Removed convertPrice function - using hardcoded prices only
  // const convertPrice = async () => {
  //   setConverting(true);
  //   try {
  //     const result = await currencyService.convertAmount(
  //       clientPrice,
  //       'USD',
  //       selectedCurrency
  //     );
  //     setConvertedPrice(result);
  //   } catch (error) {
  //     console.error('Error converting price:', error);
  //   } finally {
  //     setConverting(false);
  //   }
  // };

  const handleConfirm = () => {
    // Pass only client price - backend calculates provider cost internally
    onConfirmRenewal(selectedCurrency, clientPrice);
  };

  // Use hardcoded prices directly - no real-time conversion
  const displayPrice = `${clientPrice.toFixed(2)} ${selectedCurrency}`;


  return (
    <Dialog 
      open={open} 
      onClose={loading ? () => {} : onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          backgroundColor: '#ffffff',
          color: '#1a1a1a',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#ffffff',
        color: '#1a1a1a',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pt: 3,
        pb: 2,
        px: 3,
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          Renew Your eSIM Plan
        </Typography>
        <IconButton 
          onClick={onClose} 
          size="small"
          disabled={loading}
          sx={{ 
            color: '#6b7280', 
            '&:hover': { color: '#1a1a1a', backgroundColor: '#f3f4f6' },
            '&.Mui-disabled': { color: '#d1d5db' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2, position: 'relative' }}>
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '12px',
            }}
          >
            <CircularProgress size={48} sx={{ color: '#1a1a1a', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 600, mb: 1 }}>
              Creating Checkout Session
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', px: 4 }}>
              Please wait while we prepare your payment...
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {esimData && (
          <Box>
            {/* Current Plan Details */}
            <Typography variant="h6" sx={{ color: '#1a1a1a', mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
              Current Plan Details
            </Typography>

            <Paper 
              elevation={0}
              sx={{ 
                p: 2.5, 
                mb: 3, 
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.95rem' }}>
                  Plan
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>
                  {esimData.plan_name || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.95rem' }}>
                  Data Capacity
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>
                  {esimData.data_capacity || '5 GB'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.95rem' }}>
                  Validity
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>
                  {esimData.validity || '20 days'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.95rem' }}>
                  Provider
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.95rem' }}>
                  {esimData.api_provider || 'ExampleTel'}
                </Typography>
              </Box>
            </Paper>

            {/* Info Message */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                mb: 3, 
                backgroundColor: '#eff6ff',
                border: '1px solid #dbeafe',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <InfoIcon sx={{ color: '#3b82f6', fontSize: '1.25rem', mt: 0.2 }} />
              <Typography variant="body2" sx={{ color: '#1e40af', fontSize: '0.9rem', lineHeight: 1.5 }}>
                Your eSIM plan will be renewed immediately after payment is completed.
              </Typography>
            </Paper>

            {/* Payment Currency */}
            <Typography variant="h6" sx={{ color: '#1a1a1a', mb: 1.5, fontWeight: 600, fontSize: '1rem' }}>
              Payment Currency
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <Select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                disabled={loading}
                sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d1d5db',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1a1a1a',
                  },
                  '& .MuiSelect-select': {
                    color: '#1a1a1a',
                    fontWeight: 500,
                  },
                }}
              >
                {availableCurrencies.map((currency) => (
                  <MenuItem key={currency.code} value={currency.code}>
                    {currency.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Renewal Price */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 600, fontSize: '1rem' }}>
                Renewal Price
              </Typography>
              <Typography variant="h4" sx={{ color: '#1a1a1a', fontWeight: 700, fontSize: '2rem' }}>
                {displayPrice}
              </Typography>




            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          fullWidth
          disabled={loading}
          sx={{ 
            color: '#1a1a1a',
            borderColor: '#e5e7eb',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            borderRadius: '8px',
            fontSize: '1rem',
            '&:hover': { 
              borderColor: '#d1d5db',
              backgroundColor: '#f9fafb'
            },
            '&.Mui-disabled': {
              borderColor: '#e5e7eb',
              color: '#9ca3af',
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            backgroundColor: '#1a1a1a',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            borderRadius: '8px',
            fontSize: '1rem',
            '&:hover': {
              backgroundColor: '#2d2d2d',
            },
            '&.Mui-disabled': {
              backgroundColor: '#d1d5db',
              color: '#9ca3af',
            }
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={20} sx={{ color: 'white' }} />
              <Typography variant="body2" sx={{ color: 'white' }}>
                Preparing Checkout....
              </Typography>
            </Box>
          ) : (
            'Proceed to Payment'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimpleRenewalDialog;
